import { API_ORIGIN } from '../config';
const API_BASE = `${API_ORIGIN}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
  };
};

const handleResponse = async (res) => {
  const body = await res.json().catch(() => ({}));
  
  // Check for HTTP errors
  if (!res.ok) {
    throw new Error(body.error || body.message || `HTTP ${res.status}`);
  }
  
  // Check for application-level errors (success: false)
  if (body.success === false) {
    throw new Error(body.error || body.message || 'Request failed');
  }
  
  return body;
};

const _get  = (url)        => fetch(url, { method: 'GET',    headers: authHeaders() }).then(handleResponse);
const _post = (url, body)  => fetch(url, { method: 'POST',   headers: authHeaders(), body: JSON.stringify(body) }).then(handleResponse);
const _put  = (url, body)  => fetch(url, { method: 'PUT',    headers: authHeaders(), body: JSON.stringify(body) }).then(handleResponse);
const _del  = (url)        => fetch(url, { method: 'DELETE', headers: authHeaders() }).then(handleResponse);

const buildQs = (params = {}) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.append(k, v); });
  const s = p.toString();
  return s ? `?${s}` : '';
};

const overtimeService = {

  // ── Lookup ────────────────────────────────────────────────────
  getFestivals: () => _get(`${API_BASE}/overtime/festivals`),
  getEmployees: () => _get(`${API_BASE}/overtime/employees`),

  // ── Overtime Requests V2 ──────────────────────────────────────

  /** Create request – FM / Senior Officer (role 5 or 6) */
  createRequest: (data) => _post(`${API_BASE}/overtime`, data),

  /**
   * List requests. Optional params:
   *   status, festival_id
   */
  getRequests: (params = {}) => _get(`${API_BASE}/overtime${buildQs(params)}`),

  /** Employee's own requests */
  getMyRequests: (params = {}) => _get(`${API_BASE}/overtime/my-requests${buildQs(params)}`),

  /** Single request with history */
  getById: (id) => _get(`${API_BASE}/overtime/${id}`),

  /** Update and resubmit a request (FM/SO can amend rejected requests) */
  updateRequest: (id, data) => _put(`${API_BASE}/overtime/${id}`, data),

  // ── Coordinator ───────────────────────────────────────────────
  /** data: { decision: 'approved'|'rejected', notes? } */
  coordinatorDecision: (id, data) => _post(`${API_BASE}/overtime/${id}/coordinator-decision`, data),

  // ── Employee time tracking ────────────────────────────────────
  employeeStart:   (id) => _post(`${API_BASE}/overtime/${id}/employee-start`,   {}),
  employeeEnd:     (id) => _post(`${API_BASE}/overtime/${id}/employee-end`,     {}),
  employeeConfirm: (id) => _post(`${API_BASE}/overtime/${id}/employee-confirm`, {}),

  // ── FM / Senior Officer final approval ────────────────────────
  /**
   * Only callable after festival_date_to has passed.
   * data: { final_date, final_time_in, final_time_out, final_hours, final_task, notes? }
   */
  fmFinalApproval: (id, data) => _post(`${API_BASE}/overtime/${id}/fm-final-approval`, data),

  // ── Admin ─────────────────────────────────────────────────────
  deleteRequest: (id) => _del(`${API_BASE}/overtime/${id}`),

  // ── Coordinator management (admin only) ──────────────────────
  getCoordinators:   ()       => _get(`${API_BASE}/overtime/coordinators`),
  addCoordinator:    (data)   => _post(`${API_BASE}/overtime/coordinators`, data),
  removeCoordinator: (id)     => _del(`${API_BASE}/overtime/coordinators/${id}`),

  // ── Public Holidays (admin only) ─────────────────────────────
  getPublicHolidays:    ()       => _get(`${API_BASE}/overtime/public-holidays`),
  addPublicHoliday:     (data)   => _post(`${API_BASE}/overtime/public-holidays`, data),
  updatePublicHoliday:  (id, d)  => _put(`${API_BASE}/overtime/public-holidays/${id}`, d),
  deletePublicHoliday:  (id)     => _del(`${API_BASE}/overtime/public-holidays/${id}`),

  // ── Reports ───────────────────────────────────────────────────
  reportByFestival: (festivalId)           => _get(`${API_BASE}/overtime/reports/by-festival${buildQs({ festival_id: festivalId })}`),
  reportByEmployee: (employeeId)           => _get(`${API_BASE}/overtime/reports/by-employee${buildQs({ employee_id: employeeId })}`),
  reportSummary:    (dateFrom, dateTo)     => _get(`${API_BASE}/overtime/reports/summary${buildQs({ date_from: dateFrom, date_to: dateTo })}`),
  
  // PDF download with auth
  downloadPdf: async (id) => {
    const url = `${API_BASE}/overtime/reports/pdf/${id}`;
    const response = await fetch(url, { 
      method: 'GET', 
      headers: authHeaders() 
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || body.message || 'Failed to download PDF');
    }
    return response.blob();
  },
};

export default overtimeService;
